import { ReactNode } from 'react';
import { Icon, type IconName } from './admin/AdminUI';

interface CardProps {
  title?: string;
  children: ReactNode;
  className?: string;
  description?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
}

export function Card({ title, children, className = '', description, actions, flush = false }: CardProps) {
  return (
    <div className={`bg-white rounded-xl shadow-sm ring-1 ring-slate-200/80 ${className}`}>
      {title && (
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className={flush ? '' : 'p-5'}>{children}</div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  status?: 'success' | 'warning' | 'error' | 'neutral';
  icon?: IconName;
  hint?: ReactNode;
}

const chipStyles = {
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  warning: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  error: 'bg-red-50 text-red-700 ring-red-600/20',
  neutral: '',
};

const iconStyles = {
  success: 'bg-emerald-50 text-emerald-600',
  warning: 'bg-amber-50 text-amber-600',
  error: 'bg-red-50 text-red-600',
  neutral: 'bg-slate-100 text-slate-600',
};

/**
 * A metric tile. Status values (Connected / Not configured) render as a
 * coloured chip; counts render as a big number.
 */
export function StatCard({ label, value, status = 'neutral', icon, hint }: StatCardProps) {
  const asChip = typeof value === 'string' && status !== 'neutral' && !/^[\d.,%]+$/.test(value);
  const numberColor = {
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    error: 'text-red-600',
    neutral: 'text-slate-900',
  }[status];

  return (
    <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200/80 p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        {icon && (
          <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconStyles[status]}`}>
            <Icon name={icon} className="h-[18px] w-[18px]" />
          </span>
        )}
      </div>
      {asChip ? (
        <p className="mt-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium ring-1 ring-inset ${chipStyles[status]}`}
          >
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
            {value}
          </span>
        </p>
      ) : (
        <p className={`mt-2 text-3xl font-semibold tracking-tight ${numberColor}`}>{value}</p>
      )}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}
